import { useState, useRef } from 'react';
import { Paperclip, SendHorizontal, X, Square } from 'lucide-react';
import { uploadFile, type Attachment } from '../../lib/api';

interface Props {
  onSend: (
    content: string,
    attachments: Attachment[],
    conversationId: string,
  ) => void;
  /** Resolves the conversation to upload into, creating one on first message. */
  ensureConversation: () => Promise<string | null>;
  /** Hard block — quota exceeded / read-only. Disables typing and sending. */
  disabled?: boolean;
  /** Active turn in this conversation. Typing stays enabled; only send is gated. */
  streaming?: boolean;
  /** Aborts the in-flight turn. Only invoked while `streaming`. */
  onStop?: () => void;
}

// One file, two paths: the extracted text goes into the transcript for the
// model to read, the bytes go straight to cloud storage. fileId ties them.
async function readAttachment(
  conversationId: string,
  f: File,
): Promise<Attachment> {
  const mimeType = f.type || 'application/octet-stream';
  const isPdf =
    mimeType === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf');
  const { extractPdfText } = isPdf
    ? await import('../../lib/pdf')
    : { extractPdfText: null };

  const [fileId, content] = await Promise.all([
    uploadFile(conversationId, f),
    extractPdfText ? extractPdfText(f) : f.text(),
  ]);

  return { fileId, name: f.name, mimeType, size: f.size, content };
}

export function Composer({
  onSend,
  ensureConversation,
  disabled,
  streaming,
  onStop,
}: Props) {
  const [value, setValue] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Tracks nested dragenter/dragleave so the highlight doesn't flicker
  // as the cursor moves over child elements.
  const dragDepth = useRef(0);

  function addFiles(incoming: FileList | null) {
    if (!incoming || incoming.length === 0) return;
    setFiles((prev) => [...prev, ...Array.from(incoming)]);
    textareaRef.current?.focus();
  }

  function removeFile(index: number) {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }

  function openFileDialog() {
    fileInputRef.current?.click();
  }

  function handleDragEnter(e: React.DragEvent) {
    e.preventDefault();
    if (disabled) return;
    dragDepth.current += 1;
    setDragActive(true);
  }

  function handleDragLeave(e: React.DragEvent) {
    e.preventDefault();
    dragDepth.current -= 1;
    if (dragDepth.current <= 0) {
      dragDepth.current = 0;
      setDragActive(false);
    }
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    dragDepth.current = 0;
    setDragActive(false);
    if (disabled) return;
    addFiles(e.dataTransfer.files);
  }

  function resize() {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 144)}px`; // 144px ≈ 6 lines at 1.5rem line-height
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape' && streaming) {
      e.preventDefault();
      onStop?.();
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  }

  async function submit() {
    if (disabled || streaming) return;
    const trimmed = value.trim();
    if (!trimmed && files.length === 0) return;

    // The storage key is {tenantId}/{conversationId}/{fileId}, so the
    // conversation has to exist before we can presign anything.
    const conversationId = await ensureConversation();
    if (!conversationId) return;

    const attachments = await Promise.all(
      files.map((f) => readAttachment(conversationId, f)),
    );

    onSend(trimmed, attachments, conversationId);
    setValue('');
    setFiles([]);
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  }

  const canSend =
    !disabled && !streaming && (value.trim().length > 0 || files.length > 0);

  return (
    <div className="container mx-auto px-4 pt-2 pb-4">
      <div
        onDragEnter={handleDragEnter}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`flex flex-col gap-2 rounded-sm border bg-white px-4 py-3 shadow-sm transition-colors ${
          dragActive ? 'border-ink bg-ink/5 border-dashed' : 'border-ink/20'
        }`}
      >
        {files.length > 0 && (
          <ul className="flex flex-wrap gap-2">
            {files.map((file, i) => (
              <li
                key={`${file.name}-${i}`}
                className="bg-ink/5 text-ink/80 flex max-w-full items-center gap-1.5 rounded-sm px-2 py-1 text-xs"
              >
                <span className="truncate">{file.name}</span>
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  className="text-ink/40 hover:text-ink shrink-0"
                  aria-label={`Remove ${file.name}`}
                >
                  <X size={14} strokeWidth={2.5} />
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex items-center gap-3">
          <textarea
            ref={textareaRef}
            rows={1}
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              resize();
            }}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            placeholder="Ask anything"
            className="flex-1 resize-none overflow-y-auto bg-transparent text-sm leading-relaxed text-neutral-900 outline-none placeholder:text-neutral-400 disabled:opacity-50"
          />

          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = ''; // allow re-selecting the same file
            }}
          />
          <button
            type="button"
            onClick={openFileDialog}
            disabled={disabled}
            className="text-ink/40 hover:text-ink shrink-0 disabled:opacity-25"
            aria-label="Attach files"
          >
            <Paperclip size={18} strokeWidth={2} />
          </button>

          <button
            onClick={streaming ? onStop : submit}
            disabled={streaming ? !onStop : !canSend}
            className="text-ink flex h-8 w-8 shrink-0 items-center justify-center transition-opacity disabled:opacity-25"
            aria-label={streaming ? 'Stop generating' : 'Send message'}
          >
            {streaming ? (
              <Square size={18} strokeWidth={0} fill="currentColor" />
            ) : (
              <SendHorizontal strokeWidth={2.5} />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
